import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import PaginatedSearchList, {
  BaseFilter,
  CustomFilterComponentProps,
  DataSourceOptions,
} from "../../../components/PaginatedSearchList";
import {
  TableRow,
  renderTable,
  RowRendererProps,
} from "../../../components/PaginatedListTableRenderer";
import {
  withPaginatedItemSupport,
  ListRendererProps,
  Item,
} from "../../../components/PaginatedList";
import { RVModel } from "../../../api/connx/rvmodel";
import { RVModelClient } from "../../../api/api";
import { GetAllUserGroups, UserGroupChild } from "../../../api/usergroup";
import RVRowActions from "./RVRow";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { RBACPermission } from "../../../user/rbac";
import { anyGranted } from "../../../user/user";
import {
  Sort,
  SortHeader,
  compareText,
  nextSort,
} from "../../../components/SortHeader";

interface ModelFilterValues extends BaseFilter {
  // only the models of this user group
  groupID?: string;
}

async function getRVModels({
  filter,
  nextKey,
  filterParams,
}: DataSourceOptions<ModelFilterValues>): Promise<{
  items: (RVModel & Item)[];
  nextKey?: string;
}> {
  const r = await RVModelClient.listRVModels({
    nextKey,
    filter,
    groupID: filterParams?.groupID || undefined,
  });
  return {
    items: r.models.map((m) => withPaginatedItemSupport(m, m.docID)),
    nextKey: r.nextKey,
  };
}

type GroupFilterComponent = React.ComponentType<
  CustomFilterComponentProps<ModelFilterValues>
>;

// a filter narrowing the list to one user group's models
const groupFilter = (groups: UserGroupChild[]): GroupFilterComponent => {
  const GroupFilter = ({
    value,
    onChange,
    disabled,
  }: CustomFilterComponentProps<ModelFilterValues>): JSX.Element => (
    <Form.Control
      as="select"
      style={{ maxWidth: 360 }}
      data-testid="model-group-filter"
      value={value.groupID || ""}
      disabled={disabled}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>): void =>
        onChange({ ...value, groupID: e.target.value || undefined })
      }
    >
      <option value="">All groups</option>
      {groups.map((g) => (
        <option key={g.id} value={g.id}>
          {g.name}
        </option>
      ))}
    </Form.Control>
  );
  return GroupFilter;
};

interface RowProps {
  hasPermission?: (p: RBACPermission) => boolean;
  // names of the user groups, by ID; the group of a model is only shown
  // to users who see the models of more than one group
  groupNames?: Record<string, string>;
}

const MyRow = withRouter(
  (props: RowProps & RowRendererProps<RVModel> & RouteComponentProps) => {
    const {
      history,
      item,
      index,
      hasPermission = anyGranted,
      groupNames,
      onDeleted,
      ...childProps
    } = props;

    const handleDelete = hasPermission("rv_model_delete")
      ? (): Promise<boolean> => RVModelClient.delete(item.groupID, item.docID)
      : undefined;
    const handleEdit = hasPermission("rv_model_update")
      ? (): void =>
          history.push(
            `/connx/rvmodels/edit/model/${item.groupID}/${item.docID}`
          )
      : undefined;

    return (
      <TableRow item={item} {...childProps}>
        <div>{item.name}</div>
        <div>{item.desc}</div>
        {groupNames && (
          <div data-testid="model-group">
            {groupNames[item.groupID] || item.groupID}
          </div>
        )}
        <RVRowActions
          getItemName={(): string => item.name}
          onDelete={handleDelete}
          onDeleted={(): void => onDeleted(index)}
          onEdit={handleEdit}
        />
      </TableRow>
    );
  }
);

const TableRenderer = renderTable(MyRow);

type ModelSortKey = "name" | "desc" | "group";

// the text of a model's column, as shown
const modelText = (
  m: RVModel,
  key: ModelSortKey,
  groupNames?: Record<string, string>
): string => {
  switch (key) {
    case "desc":
      return m.desc || "";
    case "group":
      return groupNames?.[m.groupID] || m.groupID;
    default:
      return m.name || "";
  }
};

// the indices of the given models in sorted order; an empty value sorts last
// whichever the direction, and ties go by name then ID
export const sortModels = (
  list: RVModel[],
  { key, asc }: Sort<ModelSortKey>,
  groupNames?: Record<string, string>
): number[] =>
  list
    .map((_, i) => i)
    .sort((i, j) => {
      const a = modelText(list[i], key, groupNames);
      const b = modelText(list[j], key, groupNames);
      const primary =
        "" === a || "" === b
          ? Number("" === a) - Number("" === b)
          : (asc ? 1 : -1) * compareText(a, b);
      return (
        primary ||
        compareText(list[i].name || "", list[j].name || "") ||
        compareText(list[i].docID, list[j].docID)
      );
    });

export const MyTableRenderer = (
  props: ListRendererProps<RVModel> & RowProps
): JSX.Element => {
  const { list, onDeleted, ...rest } = props;
  const [sort, setSort] = useState<Sort<ModelSortKey>>({
    key: "name",
    asc: true,
  });
  const order = useMemo(() => sortModels(list || [], sort, props.groupNames), [
    list,
    sort,
    props.groupNames,
  ]);
  const sorted = useMemo(() => order.map((i) => list[i]), [order, list]);

  const handleSort = (key: ModelSortKey): void =>
    setSort((s) => nextSort(s, key));
  const header = (label: string, key: ModelSortKey): JSX.Element => (
    <SortHeader label={label} sortKey={key} sort={sort} onSort={handleSort} />
  );

  return (
    <TableRenderer
      {...rest}
      list={sorted}
      // a row reports its displayed index; the list holds them unsorted
      onDeleted={(i: number): void => onDeleted(order[i])}
    >
      {header("Name", "name")}
      {header("Description", "desc")}
      {props.groupNames && header("Group", "group")}
      <div></div>
    </TableRenderer>
  );
};

interface RVModelListProps {
  hasPermission?: (p: RBACPermission) => boolean;
  // the user groups, for users who see the models of every group
  loadGroups?: () => Promise<UserGroupChild[]>;
}

const RVModelList = ({
  hasPermission = anyGranted,
  loadGroups = GetAllUserGroups,
  ...rest
}: RVModelListProps): JSX.Element => {
  // a model/order admin sees every group's models, so needs to tell them apart
  const allGroups = hasPermission("rv_model_admin_ro");
  const [groups, setGroups] = useState<UserGroupChild[]>([]);
  useEffect(() => {
    if (!allGroups) return;
    let cancelled = false;
    loadGroups()
      .then((g) => !cancelled && setGroups(g))
      .catch((e) => {
        // the groups cannot be listed: the models are shown without them
        console.log(e);
      });
    return (): void => {
      cancelled = true;
    };
  }, [allGroups, loadGroups]);

  const groupNames = useMemo(
    () =>
      allGroups && groups.length > 0
        ? groups.reduce<Record<string, string>>(
            (acc, g) => ({ ...acc, [g.id]: g.name }),
            {}
          )
        : undefined,
    [allGroups, groups]
  );
  const GroupFilter = useMemo(
    () => (groupNames ? groupFilter(groups) : undefined),
    [groupNames, groups]
  );
  const rowProps: RowProps = { hasPermission, groupNames };

  return (
    <PaginatedSearchList
      getItems={getRVModels}
      ListRenderer={MyTableRenderer}
      CustomFilters={GroupFilter}
      {...rowProps}
      {...rest}
    />
  );
};

export default RVModelList;
